// src/components/admin/EditorTextosLegales.js
// Editor de textos configurables (T&C, políticas). Solo SuperAdmin.
// Cada guardado archiva la versión previa en el historial (backend hace todo).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Alert, Button,
  TextField, IconButton, Tooltip, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  getConfiguracionTexto, actualizarConfiguracionTexto,
  getHistorialConfiguracionTexto, getHistorialItemConfiguracionTexto,
} from '../../services/configuracionTextoApi';
import ReactMarkdown from 'react-markdown';

// Componentes MUI-styled para react-markdown
const mdComponents = {
  h1: ({ node, ...p }) => <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a4d2e', mt: 1.5, mb: 1 }} {...p} />,
  h2: ({ node, ...p }) => <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32', mt: 2, mb: 0.5 }} {...p} />,
  h3: ({ node, ...p }) => <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }} {...p} />,
  p:  ({ node, ...p }) => <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.65, color: '#333' }} {...p} />,
  ul: ({ node, ...p }) => <Box component="ul" sx={{ pl: 3, mb: 1 }} {...p} />,
  ol: ({ node, ...p }) => <Box component="ol" sx={{ pl: 3, mb: 1 }} {...p} />,
  li: ({ node, ...p }) => <Box component="li" sx={{ mb: 0.4, fontSize: '0.88rem', lineHeight: 1.6, color: '#333' }} {...p} />,
  strong: ({ node, ...p }) => <Box component="strong" sx={{ fontWeight: 700, color: '#1a1a1a' }} {...p} />,
  em: ({ node, ...p }) => <Box component="em" sx={{ fontStyle: 'italic', color: '#555' }} {...p} />,
  code: ({ node, ...p }) => <Box component="code" sx={{
    bgcolor: '#f0f0f0', px: 0.5, py: 0.2, borderRadius: 0.5,
    fontFamily: 'monospace', fontSize: '0.85em',
  }} {...p} />,
  hr: () => <Box sx={{ my: 1.5, borderBottom: '1px solid', borderColor: 'divider' }} />,
};

// Claves conocidas por ahora (se pueden agregar más cuando existan)
const CLAVES = [
  { clave: 'TYC_ALIADO', descripcion: 'Términos y Condiciones del Aliado Comercial (wizard "Aliarse a la red").' },
];

export default function EditorTextosLegales({ onToast }) {
  const [seleccion, setSeleccion] = useState(null);   // { clave, titulo, contenido, versionActual, ... }
  const [loading, setLoading]     = useState(false);

  // Edición
  const [editAbierto, setEditAbierto] = useState(false);
  const [formTitulo, setFormTitulo]   = useState('');
  const [formContenido, setFormContenido] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Historial
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [verVersion, setVerVersion] = useState(null); // { HistorialID, Contenido, ... }

  const abrirClave = useCallback(async (clave) => {
    setLoading(true);
    try {
      const t = await getConfiguracionTexto(clave);
      setSeleccion(t);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar el texto.', 'error');
      setSeleccion(null);
    } finally { setLoading(false); }
  }, [onToast]);

  const abrirEdicion = () => {
    if (!seleccion) return;
    setFormTitulo(seleccion.titulo);
    setFormContenido(seleccion.contenido);
    setEditAbierto(true);
  };

  const guardar = async () => {
    if (!seleccion) return;
    if (!formTitulo.trim() || !formContenido.trim()) {
      onToast?.('Título y contenido son obligatorios.', 'warning');
      return;
    }
    setGuardando(true);
    try {
      const r = await actualizarConfiguracionTexto(seleccion.clave, {
        titulo: formTitulo,
        contenido: formContenido,
      });
      onToast?.(`Guardado. Nueva versión: ${r.versionActual}`, 'success');
      setEditAbierto(false);
      await abrirClave(seleccion.clave);
    } catch (e) {
      onToast?.(e.message || 'No se pudo guardar.', 'error');
    } finally { setGuardando(false); }
  };

  const abrirHistorial = async () => {
    if (!seleccion) return;
    setHistorialAbierto(true);
    setHistorialLoading(true);
    try {
      const h = await getHistorialConfiguracionTexto(seleccion.clave);
      setHistorial(Array.isArray(h) ? h : []);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar el historial.', 'error');
    } finally { setHistorialLoading(false); }
  };

  const verContenidoVersion = async (item) => {
    try {
      const full = await getHistorialItemConfiguracionTexto(item.historialID);
      setVerVersion(full);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar la versión.', 'error');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <DescriptionOutlinedIcon sx={{ color: '#2e7d32' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a4d2e' }}>
          Textos legales
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Contenido de términos y condiciones, políticas y avisos legales que se muestran a los usuarios.
        Cada edición se archiva como una nueva versión — nunca se pierde la que un usuario aceptó.
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {CLAVES.map(c => (
          <Chip key={c.clave} label={c.clave}
            variant={seleccion?.clave === c.clave ? 'filled' : 'outlined'}
            color={seleccion?.clave === c.clave ? 'success' : 'default'}
            onClick={() => abrirClave(c.clave)}
            sx={{ fontWeight: 600 }}
          />
        ))}
      </Stack>

      {loading && <CircularProgress size={20} />}

      {seleccion && !loading && (
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {CLAVES.find(c => c.clave === seleccion.clave)?.descripcion}
          </Alert>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{seleccion.titulo}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Versión actual: <strong>{seleccion.versionActual}</strong>
            {' · '}Última modificación: {seleccion.fechaModificacion ? new Date(seleccion.fechaModificacion).toLocaleString() : '—'}
            {seleccion.modificadoPor && <> · por <strong>{seleccion.modificadoPor}</strong></>}
          </Typography>
          <Box sx={{
            m: 0, mb: 2,
            maxHeight: 400, overflow: 'auto',
            border: '1px solid', borderColor: 'divider', borderRadius: 1,
            p: 2, bgcolor: '#fff',
          }}>
            <ReactMarkdown components={mdComponents}>{seleccion.contenido}</ReactMarkdown>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" color="success" startIcon={<EditIcon />} onClick={abrirEdicion}>
              Editar
            </Button>
            <Button variant="outlined" startIcon={<HistoryIcon />} onClick={abrirHistorial}>
              Historial de versiones
            </Button>
          </Stack>
        </Box>
      )}

      {/* Dialog de edición */}
      <Dialog open={editAbierto} onClose={() => !guardando && setEditAbierto(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Editar {seleccion?.clave}
          <IconButton size="small" onClick={() => setEditAbierto(false)} disabled={guardando}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Al guardar se crea una nueva versión (v{(seleccion?.versionActual ?? 0) + 1}).
            La versión anterior queda archivada en el historial y sigue vinculada a los usuarios que la aceptaron.
          </Alert>
          <TextField label="Título" fullWidth sx={{ mb: 2 }}
            value={formTitulo} onChange={e => setFormTitulo(e.target.value)} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Contenido en formato <strong>Markdown</strong> — a la izquierda el editor, a la derecha la vista previa.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, minHeight: 460 }}>
            <TextField label="Contenido (Markdown)" fullWidth multiline minRows={20}
              value={formContenido} onChange={e => setFormContenido(e.target.value)}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.82rem', alignItems: 'flex-start' } }}
              sx={{ flex: 1 }}
            />
            <Box sx={{
              flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1,
              p: 2, bgcolor: '#fafafa', overflow: 'auto', maxHeight: 500,
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 700 }}>
                VISTA PREVIA
              </Typography>
              <ReactMarkdown components={mdComponents}>{formContenido}</ReactMarkdown>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAbierto(false)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={guardar} disabled={guardando}
            startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}>
            Guardar como nueva versión
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de historial */}
      <Dialog open={historialAbierto} onClose={() => setHistorialAbierto(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Historial de {seleccion?.clave}
          <IconButton size="small" onClick={() => setHistorialAbierto(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {historialLoading && <CircularProgress size={20} />}
          {!historialLoading && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Versión</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Creado por</TableCell>
                  <TableCell>Hash SHA-256</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.map(h => (
                  <TableRow key={h.historialID} hover>
                    <TableCell><strong>v{h.version}</strong></TableCell>
                    <TableCell>{h.titulo}</TableCell>
                    <TableCell>{new Date(h.fechaCreacion).toLocaleString()}</TableCell>
                    <TableCell>{h.creadoPor || '—'}</TableCell>
                    <TableCell><code style={{ fontSize: '0.7rem' }}>{h.hashContenido?.substring(0, 12)}…</code></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver contenido">
                        <IconButton size="small" onClick={() => verContenidoVersion(h)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {historial.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">Sin historial.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para ver contenido de una versión histórica */}
      <Dialog open={!!verVersion} onClose={() => setVerVersion(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {verVersion?.titulo} — v{verVersion?.version}
          <IconButton size="small" onClick={() => setVerVersion(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {verVersion && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Hash: <code>{verVersion.hashContenido}</code>
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ m: 0 }}>
                <ReactMarkdown components={mdComponents}>{verVersion.contenido}</ReactMarkdown>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
